/**
 * Registers the service worker + tracks the beforeinstallprompt event so the
 * app can show its own "Install to home screen" hint (see InstallHint.jsx).
 *
 * Notes:
 *   • CRA doesn't bundle service-worker.js, so we point directly at /service-worker.js
 *     which is served verbatim from /public. That's fine — it's plain JS, no imports.
 *   • On iOS Safari there's no beforeinstallprompt; we detect iOS separately and
 *     show a "Share → Add to Home Screen" instruction.
 */

let deferredInstallPrompt = null;

export function initPwa() {
  // Skip in unsupported environments (e.g. some code sandboxes).
  if (typeof window === "undefined") return;

  // Register the service worker.
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/service-worker.js", { scope: "/" })
        .then((reg) => {
          // If a new SW is waiting, force-activate on next boot.
          if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
          reg.addEventListener("updatefound", () => {
            const nw = reg.installing;
            if (!nw) return;
            nw.addEventListener("statechange", () => {
              if (nw.state === "installed" && navigator.serviceWorker.controller) {
                // New version installed; ask the SW to activate next reload.
                nw.postMessage({ type: "SKIP_WAITING" });
              }
            });
          });
        })
        .catch((err) => {
          console.warn("SW registration failed:", err);
        });
    });
  }

  // Capture the deferred install prompt for our own custom banner.
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    // Sync flag so components that mount AFTER the event fires can check
    // installability synchronously in useEffect.
    window.__summitInstallable = true;
    window.dispatchEvent(new CustomEvent("summit-air:installable"));
  });

  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    window.__summitInstallable = false;
    window.dispatchEvent(new CustomEvent("summit-air:installed"));
  });
}

/**
 * Triggers the browser's install UI. Returns "accepted" | "dismissed" | "unavailable".
 * On iOS there's no prompt — the InstallHint component shows the manual
 * Share-menu instructions instead.
 */
export async function triggerInstallPrompt() {
  if (!deferredInstallPrompt) return "unavailable";
  deferredInstallPrompt.prompt();
  const { outcome } = await deferredInstallPrompt.userChoice;
  deferredInstallPrompt = null;
  return outcome;
}

export function isStandaloneMode() {
  return (
    (typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)").matches)
    || (typeof navigator !== "undefined" && navigator.standalone === true)  // iOS
  );
}

export function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

/**
 * Signal the SW to clear cached API reads (e.g. on logout for privacy).
 */
export function clearPwaApiCache() {
  if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: "CLEAR_API_CACHE" });
  }
}
